import { flow, pipe } from 'fp-ts/function';
import { record } from 'fp-ts';
import { at, compose, filter, isEqual, map } from 'lodash/fp';
import { createSelector, Selector } from 'reselect';
import { ProductPricingMode } from '@mews/enums';
import { createAction, createReducer } from '@mews/framework';
import { getStringArrayProp, getStringProp } from '@mews/fp';
import { ApplicationState, CustomLink } from '@mews-commander/types';
import { GeneralEnterprise } from '@mews-commander/data-access';
import { TIMEZONE_GMT } from '@mews-commander/constants';
import { mewsEnterprise, getDurationAsMilliseconds, keyById } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import moduleSelector from '../selector';
import { CODE_ENGLISH_US } from '../constants';
import prefix from '../prefix';

export const NAME = 'enterprises';

export interface ExtendedEnterprise extends GeneralEnterprise {
    customLinks: CustomLink[];
    editableHistoryIntervalMilliseconds: number;
}

type EnterprisesState = Record<string, ExtendedEnterprise>;

const initialState: EnterprisesState = {};

const ENTERPRISES_LOADED = prefix('ENTERPRISES_LOADED');

interface EnterprisesLoadedPayload {
    customLinks: CustomLink[];
    enterprises: GeneralEnterprise[];
}

export const enterprisesLoaded = (payload: EnterprisesLoadedPayload) => createAction(ENTERPRISES_LOADED, payload);

export const enterprisesReducer = createReducer(initialState, {
    [ENTERPRISES_LOADED]: (state: EnterprisesState, { customLinks, enterprises }: EnterprisesLoadedPayload) => ({
        ...compose(
            keyById,
            map<GeneralEnterprise, ExtendedEnterprise>((enterprise) => ({
                ...enterprise,
                editableHistoryIntervalMilliseconds: Math.min(
                    getDurationAsMilliseconds(enterprise.accountingEditableHistoryWindow),
                    getDurationAsMilliseconds(enterprise.operationalEditableHistoryWindow)
                ),
                customLinks: filter(['enterpriseId', enterprise.id], customLinks),
            }))
        )(enterprises),
    }),
});

export const getEnterprises = flow(moduleSelector, (state) => state[NAME]) as Selector<
    ApplicationState,
    EnterprisesState
>;

/** @deprecated use useEnterprise instead */
export const getEnterprise = createSelector(
    getEnterprises,
    getStringProp('enterpriseId'),
    (enterprises, enterpriseId) => (enterpriseId in enterprises ? enterprises[enterpriseId] : mewsEnterprise)
);

export const doesEnterpriseExist = createSelector(
    getEnterprises,
    getStringProp('enterpriseId'),
    (enterprises, enterpriseId) => enterpriseId in enterprises
);

export const getEnterprisesByIds = createSelector(getStringArrayProp('ids'), getEnterprises, (...args) => at(...args));

/** @deprecated use useEnterprise instead */
export const getEnterpriseChainId = flow(getEnterprise, (e) => e?.chainId);
/** @deprecated use useEnterprise instead */
export const getEnterpriseName = flow(getEnterprise, (e) => e?.name);
/** @deprecated use useEnterprise instead */
export const getEnterpriseSubscriptionId = flow(getEnterprise, (e) => e?.subscriptionId);
/** @deprecated use useEnterprise instead */
export const getEnterpriseUniqueName = flow(getEnterprise, (e) => e?.uniqueName);
/** @deprecated use useEnterprise instead */
export const getEnterpriseDefaultCurrencyCode = flow(getEnterprise, (e) => e?.defaultCurrencyCode);
/** @deprecated use useEnterprise instead */
export const getEnterpriseDefaultLanguageCode = flow(getEnterprise, (e) => e?.defaultLanguageCode || CODE_ENGLISH_US);
/** @deprecated use useEnterprise instead */
export const getEnterpriseImageId = flow(getEnterprise, (e) => e?.logoImageId);
/** @deprecated use useEnterprise instead */
export const getEnterpriseAcceptedCurrencyCodes = flow(getEnterprise, (e) => e?.acceptedCurrencyCodes);
/** @deprecated use useEnterprise instead */
export const getEnterpriseEditableHistoryIntervalMilliseconds = flow(
    getEnterprise,
    (e) => e.editableHistoryIntervalMilliseconds
);
/** @deprecated use useEnterprise instead */
export const getEnterpriseOperationalEditableHistoryWindow = flow(
    getEnterprise,
    (e) => e?.operationalEditableHistoryWindow
);
/** @deprecated use useEnterprise instead */
export const getEnterpriseTimeZoneIdentifier = flow(getEnterprise, (e) => e?.timeZoneIdentifier || TIMEZONE_GMT);
/** @deprecated use useEnterprise instead */
export const getEnterpriseCustomLinks = flow(getEnterprise, (e) => e?.customLinks);
/** @deprecated use useEnterprise instead */
export const getEnterpriseCreatedUtc = flow(getEnterprise, (e) => e?.createdUtc);
/** @deprecated use useEnterprise instead */
export const getEnterpriseLegalEnvironmentCode = flow(getEnterprise, (e) => e?.legalEnvironmentCode);
/** @deprecated use useEnterprise instead */
export const getEnterpriseTaxEnvironmentCode = flow(getEnterprise, (e) => e?.taxEnvironmentCode);
/** @deprecated use useEnterprise instead */
export const getEnterpriseAccountingEnvironmentCode = flow(getEnterprise, (e) => e?.accountingEnvironmentCode || '');
/** @deprecated use useEnterprise instead */
export const getEnterprisePricingMode = flow(getEnterprise, (e) => e?.pricing);
/** @deprecated use useEnterprise instead */
export const getEnterpriseDefaultCultureName = flow(getEnterprise, (e) => e?.defaultCultureName || CODE_ENGLISH_US);
/** @deprecated use useEnterprise instead */
export const getEnterpriseAccommodationEnvironmentCode = flow(getEnterprise, (e) => e?.accommodationEnvironmentCode);
/** @deprecated use useEnterprise instead */
export const getEnterpriseIsPartOfPortfolio = flow(getEnterprise, (e) => e?.isPartOfPortfolio);

/** @deprecated use useEnterprise instead */
export const isEnterpriseMultiTaxEnvironment = compose(isEqual(ProductPricingMode.Net), getEnterprisePricingMode);

/** @deprecated use `useCurrentEnterprise()?.isPartOfPortfolio` instead */
export const isCurrentEnterprisePartOfPortfolio = (state: ApplicationState) =>
    pipe(state, getCurrentEnterpriseId, (enterpriseId) => getEnterpriseIsPartOfPortfolio(state, { enterpriseId }));

export const isAuthenticatorSetupEnforced = createSelector(
    getEnterprises,
    record.some((enterprise) => enterprise.authenticatorSetupEnforced === true)
);
